import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { mutate } from "swr";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { apiFetch } from "../../api/client";
import { usePaperStatus, usePaperRatings } from "../../api/hooks";
import { advanceTour } from "../GuidedTour";
import { useTourStore } from "../../state/tourStore";
import type { Paper, PaperStatusSnapshot, Project } from "../../api/types";
import { formatNp } from "../../lib/score";
import { toast } from "../../lib/toast";
import { DeletePaperModal } from "./DeletePaperModal";
import { CardLabel, DetailsCard } from "./primitives/DetailsCard";
import { MetaChip, MetaRow } from "./primitives/MetaRow";
import { StatusBadge } from "./primitives/StatusBadge";
import { WipeAnnotationsModal } from "./WipeAnnotationsModal";

const STAGE_ORDER = ["ocr", "language_analysis", "content_analysis"] as const;
type StageName = (typeof STAGE_ORDER)[number];

const STAGE_KEY: Record<StageName, string> = {
  ocr: "paper_panel.stage_ocr",
  language_analysis: "paper_panel.stage_language_analysis",
  content_analysis: "paper_panel.stage_content_analysis",
};

const STAGE_DONE_KEY: Record<StageName, keyof Paper> = {
  ocr: "ocr_done",
  language_analysis: "language_done",
  content_analysis: "content_done",
};

/** Minimum ms each stage spinner stays visible — ensures even fast stages are seen. */
const MIN_STAGE_MS = 600;

type StepState = "done" | "active" | "waiting" | "error";

const STATUS_DONE_KEY: Record<StageName, "ocr_done" | "language_done" | "content_done"> = {
  ocr: "ocr_done",
  language_analysis: "language_done",
  content_analysis: "content_done",
};

function stepState(
  stage: StageName,
  paper: Paper,
  statusData: PaperStatusSnapshot | undefined,
  displayStage: string | null,
  isProcessing: boolean,
): StepState {
  if (isProcessing && displayStage === stage) return "active";
  // Prefer fresh done flags from the status poll; fall back to the project-cache row.
  const isDone = statusData != null
    ? statusData[STATUS_DONE_KEY[stage]]
    : Boolean(paper[STAGE_DONE_KEY[stage]]);
  if (isDone) return "done";
  if (
    (paper.status === "failed" || paper.status === "quota_exceeded") &&
    paper.failed_stage === stage
  )
    return "error";
  return "waiting";
}

export function PaperPanel({ project, paper }: { project: Project; paper: Paper }) {
  const { t } = useTranslation();
  const { data: statusData } = usePaperStatus(paper.id);
  const [reprocessing, setReprocessing] = useState(false);
  const [runningStage, setRunningStage] = useState<StageName | null>(null);
  const [showWipe, setShowWipe] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [, setParams] = useSearchParams();

  // Drive processing state from the live status poll, not just the project-cache paper row.
  const liveStatus = statusData?.status;
  // A freshly-uploaded paper has status "queued" before the user triggers the pipeline.
  // Don't treat that as "running" — only treat "queued" as processing if the paper has
  // previously completed at least one stage (i.e. this is a re-queue, not a fresh upload).
  const neverProcessed =
    paper.status === "queued" && !paper.ocr_done && !paper.language_done && !paper.content_done;
  // Use liveStatus authoritatively when loaded; fall back to paper.status only while
  // statusData is still loading. This prevents a stale paper.status="processing" from
  // keeping the spinner alive after the poll confirms the pipeline is done.
  const isProcessing = liveStatus != null
    ? liveStatus === "staged" || liveStatus === "processing" || (liveStatus === "queued" && !neverProcessed)
    : paper.status === "staged" || paper.status === "processing" || (paper.status === "queued" && !neverProcessed);
  const liveStage = statusData?.stage ?? null;
  const isAnyRunning = isProcessing || reprocessing || runningStage !== null;

  // Clear reprocessing once the status poll confirms the run has finished.
  useEffect(() => {
    if (!reprocessing) return;
    if (liveStatus && liveStatus !== "queued" && liveStatus !== "staged" && liveStatus !== "processing") {
      setReprocessing(false);
    }
  }, [liveStatus, reprocessing]);

  // When the live status transitions to a terminal state, refresh the projects cache so
  // paper.status (and derived UI like showReview) picks up the new value without a page reload.
  useEffect(() => {
    if (!liveStatus || liveStatus === "staged" || liveStatus === "processing" || liveStatus === "queued") return;
    void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
  }, [liveStatus]);

  // ── displayStage: replay logic ────────────────────────────────
  // The UI shows a spinner per stage for at least MIN_STAGE_MS. If two stages
  // complete between polls (e.g. OCR + language both finish in one 1 s window)
  // we play them through sequentially so the user sees every step.
  //
  // lastShownIdxRef tracks the last stage index we displayed:
  //   -2 = uninitialised (first mount — snap to current state, no replay)
  //   -1 = reset (reprocess clicked — replay from the start)
  const [displayStage, setDisplayStage] = useState<string | null>(null);
  const lastShownIdxRef = useRef<number>(-2);

  // Reset when a new run starts.
  useEffect(() => {
    if (liveStatus === "queued" || liveStatus === "staged") {
      lastShownIdxRef.current = -1;
      setDisplayStage(null);
    }
  }, [liveStatus]);

  // Advance displayStage whenever liveStage moves forward.
  useEffect(() => {
    if (!liveStage) return;
    const liveIdx = (STAGE_ORDER as readonly string[]).indexOf(liveStage);
    if (liveIdx < 0) return;

    if (lastShownIdxRef.current === -2) {
      // First mount: snap directly to current stage without replaying history.
      lastShownIdxRef.current = liveIdx;
      setDisplayStage(liveStage);
      return;
    }

    if (liveIdx <= lastShownIdxRef.current) return; // already at or past this stage

    // Stages we haven't shown yet (may be >1 if the poll skipped a fast stage).
    const toShow = Array.from(
      STAGE_ORDER.slice(lastShownIdxRef.current + 1, liveIdx + 1) as readonly string[],
    );

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    toShow.forEach((stage, i) => {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setDisplayStage(stage);
          lastShownIdxRef.current = (STAGE_ORDER as readonly string[]).indexOf(stage);
        }, i * MIN_STAGE_MS),
      );
    });
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [liveStage]);

  const showReview =
    paper.status === "completed" || paper.status === "in_review" || paper.status === "ready";
  const hasMeta = paper.page_count != null || paper.word_count != null;

  // Use fresh done flags from the status poll when available.
  const ocrDone = statusData?.ocr_done ?? Boolean(paper.ocr_done);
  const langDone = statusData?.language_done ?? Boolean(paper.language_done);
  const contentDone = statusData?.content_done ?? Boolean(paper.content_done);

  // 1/3 per completed stage. While processing, a stage that is still spinning is
  // not yet counted (prevents the bar jumping to 100% while the content spinner is
  // still active). Once processing ends, ignore displayStage so the bar reaches 100%.
  const donePct =
    contentDone && (!isProcessing || displayStage !== "content_analysis") ? 100 :
    langDone && (!isProcessing || displayStage !== "language_analysis") ? 67 :
    ocrDone && (!isProcessing || displayStage !== "ocr") ? 33 :
    0;
  const livePage = statusData?.page ?? null;
  const liveTotal = statusData?.of ?? null;
  const pct =
    isProcessing && liveStage === "ocr" && livePage != null && liveTotal != null && liveTotal > 0
      ? Math.round((livePage / liveTotal) * 33)
      : donePct;

  const showBar = paper.ocr_done || paper.language_done || paper.content_done || isProcessing;
  const statusMsg = isAnyRunning ? deriveStatusMessage(statusData, displayStage, t) : null;

  function onStartRename() {
    setNameInput(paper.name);
    setRenaming(true);
  }

  function onCancelRename() {
    setRenaming(false);
    setNameInput("");
  }

  async function onSaveRename() {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === paper.name) { onCancelRename(); return; }
    setSavingName(true);
    try {
      const res = await apiFetch(`/api/papers/${paper.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
      setRenaming(false);
    } catch {
      toast(t("common.error"));
    } finally {
      setSavingName(false);
    }
  }

  async function onRunAll() {
    setReprocessing(true);
    try {
      const endpoint = neverProcessed
        ? `/api/papers/${paper.id}/process`
        : `/api/papers/${paper.id}/reprocess`;
      const res = await apiFetch(endpoint, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
      // Force immediate repoll so the 3 s interval starts right away.
      void mutate(`/api/papers/${paper.id}/status`);
      // Keep reprocessing=true — the effect above clears it when done.
    } catch {
      toast(t("paper_panel.status_processing") + " failed — check server logs");
      setReprocessing(false);
    }
  }

  async function onRunStage(stage: StageName) {
    setRunningStage(stage);
    try {
      const res = await apiFetch(`/api/papers/${paper.id}/run_stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
      void mutate(`/api/papers/${paper.id}/status`);
    } catch {
      toast(t("paper_panel.step_failed") + " — check server logs");
    } finally {
      setRunningStage(null);
    }
  }

  async function onSave() {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/papers/${paper.id}/backup`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast((body as { detail?: string }).detail ?? "Backup failed");
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `${paper.name}.ophelia`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast("Backup failed — check server logs");
    } finally {
      setSaving(false);
    }
  }

  function onDeleted() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("paper");
      return next;
    });
  }

  return (
    <div className="details-content">
      <div className="details-title-row">
        {renaming ? (
          <>
            <input
              className="rename-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void onSaveRename();
                if (e.key === "Escape") onCancelRename();
              }}
              autoFocus
              disabled={savingName}
            />
            <div className="rename-actions">
              <button type="button" className="btn-secondary" onClick={() => void onSaveRename()} disabled={savingName}>
                {t("common.save")}
              </button>
              <button type="button" className="btn-ghost" onClick={onCancelRename} disabled={savingName}>
                {t("common.cancel")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="details-title">{paper.name}</div>
            <button type="button" className="rename-btn" onClick={onStartRename}>
              {t("common.rename_btn")}
            </button>
          </>
        )}
      </div>
      <div className="details-status-line">
        <StatusBadge status={paper.status} />
      </div>
      <div className="details-subtitle">{project.name}</div>

      {hasMeta && (
        <MetaRow>
          {paper.page_count != null && <MetaChip value={paper.page_count} label="pages" />}
          {paper.word_count != null && <MetaChip value={paper.word_count} label="words" />}
        </MetaRow>
      )}

      <DetailsCard label="Score">
        <ScoreContent project={project} paper={paper} />
      </DetailsCard>

      <div className="details-card">
        <CardLabel>{t("paper_status.processing")}</CardLabel>
        <div className="proc-steps">
          {STAGE_ORDER.map((stage) => {
            const state = stepState(stage, paper, statusData, displayStage, isProcessing);
            const stageRunning =
              runningStage === stage || (isProcessing && liveStage === stage);
            const STAGE_DURATION_KEY: Record<StageName, "ocr_duration_s" | "language_duration_s" | "content_duration_s"> = {
              ocr: "ocr_duration_s",
              language_analysis: "language_duration_s",
              content_analysis: "content_duration_s",
            };
            const duration = statusData?.[STAGE_DURATION_KEY[stage]] ?? null;
            const tokenTooltip = stage === "content_analysis" ? contentTokenTooltip(statusData) : undefined;
            return (
              <ProcStep
                key={stage}
                state={state}
                name={t(STAGE_KEY[stage])}
                running={stageRunning}
                disabled={isAnyRunning}
                isDone={state === "done"}
                durationS={duration}
                tokenTooltip={tokenTooltip}
                onRun={() => void onRunStage(stage)}
              />
            );
          })}
        </div>
        {showBar && (
          <>
            <div className="proc-progress-track">
              <div className="proc-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="proc-progress-pct">{pct}%</div>
          </>
        )}
        <div className="proc-run-all-row">
          <button
            type="button"
            className="btn-secondary"
            disabled={isAnyRunning}
            onClick={() => void onRunAll()}
          >
            {neverProcessed ? t("paper_panel.process_btn") : t("paper_panel.reprocess_btn")}
          </button>
          {statusMsg && <span className="proc-status-msg">{statusMsg}</span>}
        </div>
      </div>

      {showReview && (
        <Link
          className="review-btn-large"
          to={`/review/${paper.id}`}
          data-tour="go-to-review-btn"
          onClick={() => {
            const { active, papers } = useTourStore.getState();
            if (active && papers && paper.id === papers.inReview) {
              advanceTour();
            }
          }}
        >
          {t("paper_panel.go_to_review")} <span className="review-btn-arrow">→</span>
        </Link>
      )}

      <div className="details-divider" />

      <div className="paper-bottom-row">
        <button type="button" className="btn-ghost" onClick={() => onExport(paper, t)}>
          {t("paper_panel.export_btn")}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={saving}
          onClick={() => void onSave()}
        >
          {saving ? t("paper_panel.saving") : t("paper_panel.save_btn")}
        </button>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setShowWipe(true)}
        >
          {t("paper_panel.clear_annotations")}
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={() => setShowDelete(true)}
        >
          {t("paper_panel.delete_btn")}
        </button>
      </div>

      {showWipe && (
        <WipeAnnotationsModal
          paperId={paper.id}
          paperName={paper.name}
          onClose={() => setShowWipe(false)}
        />
      )}
      {showDelete && (
        <DeletePaperModal
          paperId={paper.id}
          paperName={paper.name}
          onClose={() => setShowDelete(false)}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}

function ProcStep({
  state,
  name,
  running,
  disabled,
  isDone,
  durationS,
  tokenTooltip,
  onRun,
}: {
  state: StepState;
  name: string;
  running: boolean;
  disabled: boolean;
  isDone: boolean;
  durationS: number | null;
  tokenTooltip?: string;
  onRun: () => void;
}) {
  const { t } = useTranslation();
  const statusText =
    state === "done"
      ? durationS != null
        ? t("paper_panel.step_complete_in", { seconds: durationS.toFixed(1) })
        : t("paper_panel.step_complete")
      : state === "active"
        ? t("paper_panel.step_in_progress")
        : state === "error"
          ? t("paper_panel.step_failed")
          : t("paper_panel.step_pending");

  return (
    <div className="proc-step">
      {state === "active" ? (
        <span className="step-spin" aria-hidden="true" />
      ) : (
        <span
          className={`step-icon ${
            state === "done" ? "step-done" : state === "error" ? "step-error" : "step-waiting"
          }`}
        >
          {state === "done" ? "✓" : state === "error" ? "✕" : "○"}
        </span>
      )}
      <span className={`step-name${state === "done" ? " done" : ""}`}>{name}</span>
      <span
        className={`step-status-text${state === "active" ? " active-text" : ""}`}
        title={state === "done" && tokenTooltip ? tokenTooltip : undefined}
      >
        {statusText}
      </span>
      <button
        type="button"
        className="step-run-btn"
        disabled={disabled}
        onClick={onRun}
      >
        {running ? "…" : isDone ? t("paper_panel.rerun_btn") : t("paper_panel.run_btn")}
      </button>
    </div>
  );
}

function deriveStatusMessage(
  snap: PaperStatusSnapshot | undefined,
  displayStage: string | null,
  t: TFunction,
): string | null {
  if (!snap) return null;
  const { status, page, of: total, processing_detail } = snap;
  if (status === "staged") return t("paper_panel.status_staged");
  if (status === "queued") return t("paper_panel.status_queued");
  if (status === "processing") {
    if (displayStage === "ocr") {
      if (processing_detail === "ocr_sending") return t("paper_panel.status_sending_to_ocr");
      if (processing_detail === "ocr_waiting") return t("paper_panel.status_ocr_waiting");
      if (page != null && total != null) return t("paper_panel.status_ocr_pages", { page, total });
      return t("paper_panel.status_sending_to_ocr");
    }
    if (displayStage === "language_analysis") return t("paper_panel.status_running_lt");
    if (displayStage === "content_analysis") return t("paper_panel.status_analyzing");
    return t("paper_panel.status_processing");
  }
  return null;
}

function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function contentTokenTooltip(snap: PaperStatusSnapshot | undefined): string | undefined {
  if (!snap) return undefined;
  const { content_input_tokens: inp, content_output_tokens: out, content_cache_read_tokens: cached } = snap;
  if (inp == null && out == null) return undefined;
  const parts: string[] = [];
  if (inp != null) parts.push(`${formatTokens(inp)} in`);
  if (out != null) parts.push(`${formatTokens(out)} out`);
  if (cached != null && cached > 0) parts.push(`${formatTokens(cached)} cached`);
  return parts.join(" · ");
}

function ScoreContent({ project, paper }: { project: Project; paper: Paper }) {
  const { t } = useTranslation();
  const { data } = usePaperRatings(paper.id);
  const result = data?.result ?? null;

  const overridden = paper.score_override != null;
  const finalNp = overridden ? paper.score_override : result?.final_np ?? null;

  if (finalNp == null && !result) {
    return <div className="score-empty">{t("score.not_calculated")}</div>;
  }
  const fmt = (n: number | null | undefined) => (n == null ? "—" : Math.round(n * 10) / 10);
  const label = formatNp(finalNp, project.score_format).label ?? "—";
  const pct = finalNp != null ? Math.round((finalNp / 15) * 100) : 0;
  return (
    <>
      <div className="score-display">
        <span className="score-grade">{label}</span>
        <span className="score-pts">
          {fmt(finalNp)} {t("score.np_unit")}
          {result?.grade_label ? ` · ${result.grade_label}` : ""}
        </span>
      </div>
      <div className="dp-score-bar-track">
        <div className="dp-score-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="dp-score-sub">
        {t("score.raster_breakdown", { language: fmt(result?.language_np), content: fmt(result?.content_np) })}
        {result && !result.complete && !overridden ? ` — ${t("score.raster_incomplete")}` : ""}
      </div>
    </>
  );
}

function onExport(paper: Paper, t: TFunction) {
  if (paper.exported_at == null) {
    toast(t("paper_panel.export_hint"));
    return;
  }
  const link = document.createElement("a");
  link.href = `/api/papers/${paper.id}/export?ts=${Date.now()}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
